import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { ChipListProps } from '@components/ChipList'
import SelectWithLabeledValue, { SelectOption } from '@components/SelectWithLabeledValue'
import useAlertDataForProfiles from '@modules/alerts/hooks/useAlertDataForProfiles'
import useContactWithProfilesCounts from '@modules/contacts/hooks/useContactWithProfilesCounts'
import useContactable from '@modules/profile/hooks/useContactable'
import EventSelectFilter from '@modules/search/components/ResultsSection/components/EventSelectFilter'
import { SEARCH_GROUP_BY_OPTIONS, SORT_SEARCH_OPTIONS } from '@modules/search/constants'
import { SearchRequestBody } from '@modules/search/hooks/useSearchProfiles'
import {
  AnyResultProfile,
  GroupedResultProfile,
  HistoryFilterOptions,
  MetaFilterSelectedData,
  MetaFilterTypes,
  NumbersRange,
  ProfileType,
  SearchProfilesResponse,
  SelectedFiltersMapByProfiles,
  UserEventsOptions,
} from '@modules/search/types'
import Pagination from '@mui/material/Pagination'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { truncateWithDots } from '@shared/utils'

import GroupedResultProfileList from './components/GroupedResultProfileList'
import MetaFilterSelect from './components/MetaFilterSelect'
import ResultChips from './components/ResultChips'
import ResultProfileList from './components/ResultProfileList'
import SearchResultPlaceholder from './components/SearchResultPlaceholder'
import {
  SearchResultsFiltersWrap,
  SearchResultsSectionInnerWrapper,
  SearchResultsSectionWrapper,
  SearchResultsTopPanel,
  TopPaginationWrapper,
} from './index.styles'
import { getCompanyId } from './util/ResultProfile.util'

export interface ResultsSectionProps {
  resultProfiles: SearchProfilesResponse
  onDeleteChip: ChipListProps['onDelete']
  searchString: string
  total: number
  selectedFiltersByProfiles: SelectedFiltersMapByProfiles
  totalPages: number
  page: number
  setPage: (page: number) => void
  setScrollX: (page: number) => void
  setScrollY: (page: number) => void
  onSort: (id: number, withPageReset?: boolean) => void
  onGroup: (id: number, withPageReset?: boolean) => void
  selectedSortId: number
  sortDisabled: boolean
  eventsYears?: NumbersRange
  selectedMetaFilter: MetaFilterSelectedData
  onSubmitMetaFilter: (data: MetaFilterSelectedData) => void
  isGroupByDisabled: boolean
  selectedGroupingId?: number
  isResultGrouped?: boolean
  isFilterApplied?: boolean
  appliedSavedSearchName?: string
  onProfileNavigate?: (id: string | number) => void
  savedSearchId?: number
  userEventsOptions?: UserEventsOptions[]
  selectedHistoryTypeId?: Nullable<number>
  handleHistoryTypeSelected?: (id: number | undefined) => void
  handleSubmitEventsFilter: (data: { excludeIds: number[]; includeIds: number[] }) => void
  eventsFilterData: { excludeIds: number[]; includeIds: number[] } | null
  searchBody?: SearchRequestBody
}

const ResultsSection = (props: ResultsSectionProps) => {
  const {
    resultProfiles,
    searchString,
    onDeleteChip,
    total,
    totalPages,
    page,
    setPage,
    setScrollX,
    setScrollY,
    selectedFiltersByProfiles = {} as SelectedFiltersMapByProfiles,
    onSort,
    selectedSortId,
    sortDisabled,
    eventsYears,
    selectedMetaFilter,
    onSubmitMetaFilter,
    isGroupByDisabled,
    onGroup,
    selectedGroupingId,
    isResultGrouped,
    isFilterApplied,
    appliedSavedSearchName,
    onProfileNavigate,
    savedSearchId,
    userEventsOptions = [],
    selectedHistoryTypeId = 0,
    handleHistoryTypeSelected,
    handleSubmitEventsFilter,
    eventsFilterData,
    searchBody,
  } = props

  const { t } = useTranslation()

  const { hasAccessToHistoryFiltering, hasAccessToAlerts, hasAccessToSearch, hasAccessToEventFiltering } = useFeatureFlags()

  const countsByCompanyId = useContactWithProfilesCounts({ profiles: resultProfiles })
  const profiles = useMemo(() => {
    if (isResultGrouped) {
      return (resultProfiles as GroupedResultProfile[]).flatMap(profile => [profile.company, ...profile.profiles])
    }
    return resultProfiles as AnyResultProfile[]
  }, [resultProfiles, isResultGrouped])

  const { profileAlerts, profilesAlertsMap } = useAlertDataForProfiles(profiles, hasAccessToAlerts && !!profiles?.length)

  const contactableMap = useContactable(
    resultProfiles.map(i => Number(getCompanyId((i as GroupedResultProfile)?.company || i))).filter(id => !!id),
  )

  const formatChipsFromFilters = (filterList: { field: string; value: string }[], profileType: ProfileType) =>
    filterList.map(({ value, field }) => ({
      label: truncateWithDots(value.replace(/^[^::]*::/, '')),
      key: value,
      metaData: { filterType: profileType || '', field },
    }))

  const chips = useMemo(() => {
    const filterEntries = Object.entries(selectedFiltersByProfiles)

    return filterEntries.flatMap(([profile, selectedProfileFilters]) =>
      formatChipsFromFilters(selectedProfileFilters, profile as ProfileType),
    )
  }, [selectedFiltersByProfiles])

  const historyFilterOptions = useMemo(() => {
    const filterOptions: SelectOption<{ exclude: boolean; onlyMine?: boolean; hasAttended?: boolean }>[] = []

    if (!hasAccessToHistoryFiltering) {
      filterOptions.push({
        id: HistoryFilterOptions.PERSONAL_HISTORY,
        label: translationKeys.SEARCH_HISTORY_FILTER_PERSONAL,
        data: { exclude: false, onlyMine: false, hasAttended: true },
      })
    } else {
      filterOptions.push(
        {
          id: HistoryFilterOptions.MY_MEETINGS,
          label: translationKeys.SEARCH_HISTORY_FILTER_MY_MEETINGS,
          data: { exclude: false, onlyMine: true },
        },
        {
          id: HistoryFilterOptions.COMPANY_MEETINGS,
          label: translationKeys.SEARCH_HISTORY_FILTER_COMPANY_HISTORY,
          data: { exclude: false, onlyMine: undefined },
        },
      )
    }

    return [
      ...filterOptions,
      {
        id: HistoryFilterOptions.NO_HISTORY,
        label: translationKeys.SEARCH_HISTORY_FILTER_NO_HISTORY,
        data: { exclude: true, onlyMine: undefined },
      },
    ]
  }, [!hasAccessToHistoryFiltering])

  const handlePaginationChange = (e: React.ChangeEvent<unknown>, v: number) => {
    setPage(v)

    if (window.scrollY > 250) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleHistorySelect = (id: number) => {
    if (handleHistoryTypeSelected) {
      handleHistoryTypeSelected(id || undefined)
    }
  }

  const isSearchResultEmpty = !chips.length && !searchString
  return (
    <SearchResultsSectionWrapper>
      <SearchResultsSectionInnerWrapper>
        {!!(chips.length || searchString) && (
          <ResultChips
            chips={chips}
            onDelete={onDeleteChip}
            resultCount={total}
            searchString={searchString}
            isFilterApplied={isFilterApplied}
            appliedSavedSearchName={appliedSavedSearchName}
            searchBody={searchBody}
          />
        )}
        {isSearchResultEmpty && <SearchResultPlaceholder />}

        {(!isSearchResultEmpty ||
          totalPages >= 1 ||
          selectedMetaFilter.value ||
          selectedMetaFilter.type === MetaFilterTypes.BY_NEW_ITEMS) && (
          <SearchResultsTopPanel>
            <TopPaginationWrapper>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePaginationChange}
                  data-testid="search_result_pagination"
                />
              )}
            </TopPaginationWrapper>
            <SearchResultsFiltersWrap>
              {hasAccessToSearch && (
                <SelectWithLabeledValue
                  label={t(translationKeys.SEARCH_BY_HISTORY)}
                  emptySelection
                  emptySelectionLabel={t(translationKeys.SEARCH_HISTORY_FILTER_REMOVE)}
                  options={historyFilterOptions}
                  selectedOptionId={selectedHistoryTypeId || 0}
                  onSelect={handleHistorySelect}
                />
              )}
              {hasAccessToEventFiltering && (
                <EventSelectFilter
                  eventsOptions={userEventsOptions}
                  onSubmit={handleSubmitEventsFilter}
                  eventsFilterData={eventsFilterData}
                />
              )}
              {eventsYears && (
                <MetaFilterSelect
                  isFilterByNewEnabled={!!appliedSavedSearchName}
                  selectedFilter={selectedMetaFilter}
                  handleSelectMetaFilter={onSubmitMetaFilter}
                  eventsDatesRange={eventsYears}
                />
              )}
              <SelectWithLabeledValue
                disabled={sortDisabled}
                label={t(translationKeys.SEARCH_SORT_BY)}
                onSelect={value => onSort(value as number)}
                options={SORT_SEARCH_OPTIONS}
                selectedOptionId={selectedSortId}
              />

              <SelectWithLabeledValue
                disabled={isGroupByDisabled}
                label={t(translationKeys.SEARCH_GROUP_BY)}
                onSelect={value => onGroup(value as number)}
                options={SEARCH_GROUP_BY_OPTIONS}
                emptySelection
                emptySelectionLabel={t(translationKeys.NO_GROUPING)}
                selectedOptionId={selectedGroupingId}
              />
            </SearchResultsFiltersWrap>
          </SearchResultsTopPanel>
        )}
        {!isSearchResultEmpty &&
          (isResultGrouped ? (
            <GroupedResultProfileList
              groups={resultProfiles as GroupedResultProfile[]}
              companyContactsMap={countsByCompanyId}
              contactableMap={contactableMap}
              setScrollX={setScrollX}
              setScrollY={setScrollY}
            />
          ) : (
            <ResultProfileList
              profiles={resultProfiles as AnyResultProfile[]}
              companyContactsMap={countsByCompanyId}
              contactableMap={contactableMap}
              onProfileNavigate={onProfileNavigate}
              savedSearchId={savedSearchId}
              profilesAlertsMap={profilesAlertsMap}
              setScrollX={setScrollX}
              setScrollY={setScrollY}
            />
          ))}

        {!isSearchResultEmpty && totalPages > 1 && profileAlerts && (
          <Pagination count={totalPages} page={page} onChange={handlePaginationChange} />
        )}
      </SearchResultsSectionInnerWrapper>
    </SearchResultsSectionWrapper>
  )
}

export default memo(ResultsSection)
